/**
 * File Management Service
 * Handles API requests related to file uploads, downloads, and management
 */
import { createService } from './serviceFactory';

// Define the base URL for file management service
const baseUrl = '/api/supplier/file-management';

// Create base service
const baseService = createService(baseUrl);

const fileManagementService = {
  /**
   * Upload files to the server using base64 encoding
   * @param {string} quotationId - The quotation ID to associate files with
   * @param {Array} files - Array of file objects to upload (either File objects or prepared DTOs)
   * @param {string} tableName - Optional table name to associate files with
   * @returns {Promise} - Response with uploaded file details
   */
  uploadFiles(quotationId, files, tableName = "Quotations") {
    if (!quotationId) {
      console.error('uploadFiles: quotationId is required');
      return Promise.reject(new Error('Quotation ID is required'));
    }
    
    if (!files || !Array.isArray(files) || files.length === 0) {
      console.error('uploadFiles: files array is required');
      return Promise.reject(new Error('Files are required'));
    }
    
    // If files are already prepared DTOs (have base64Content)
    if (files[0].base64Content) {
      // Ensure each file has the correct tableName
      const filesWithTableName = files.map(file => ({
        ...file,
        rowID: quotationId,
        tableName: tableName
      }));
      
      return baseService.client.post(`${baseUrl}/upload/${quotationId}`, filesWithTableName)
        .then(response => response.data);
    }
    
    // Otherwise, prepare the DTOs first (convert files to base64)
    return this.prepareFileUploadDtos(files, quotationId)
      .then(preparedFiles => {
        // Update tableName if different from default
        if (tableName !== "Quotations") {
          preparedFiles = preparedFiles.map(file => ({
            ...file,
            tableName: tableName
          }));
        }
        
        return baseService.client.post(`${baseUrl}/upload/${quotationId}`, preparedFiles)
      .then(response => response.data);
      });
  },
  
  /**
   * Upload files directly using FormData (more efficient for larger files)
   * @param {string} quotationId - The quotation ID to associate files with
   * @param {FileList|Array} files - FileList or Array of File objects from input
   * @returns {Promise} - Response with uploaded file details
   */
  uploadFilesFormData(quotationId, files) {
    if (!quotationId) {
      console.error('uploadFilesFormData: quotationId is required');
      return Promise.reject(new Error('Quotation ID is required'));
    }
    
    if (!files || files.length === 0) {
      console.error('uploadFilesFormData: files are required');
      return Promise.reject(new Error('Files are required'));
    }
    
    const formData = new FormData();
    
    // Add each file to the FormData object
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    // Use axios directly since we need to set special headers
    return baseService.client({
      method: 'post',
      url: `${baseUrl}/upload/${quotationId}`,
      data: formData,
      headers: {
        // Don't set Content-Type here - browser will set it with boundary
        'Content-Type': undefined
      }
    })
      .then(response => response.data)
      .catch(error => {
        console.error('uploadFilesFormData error:', error);

        // Try to surface a friendly backend message if available
        const backendMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message;

        // Wrap in a clean Error so composables/components can show a nice message
        const wrappedError = new Error(
          backendMessage || 'Failed to upload files'
        );
        wrappedError.originalError = error;
        throw wrappedError;
      });
  },
  
  /**
   * Get list of files for a quotation or tenant
   * @param {string} quotationId - The quotation ID or tenant ID used as rowID
   * @returns {Promise} - Response with file list
   */
  getFiles(quotationId) {
    if (!quotationId) {
      console.error('getFiles: quotationId is required');
      return Promise.reject(new Error('Quotation ID is required'));
    }
    
    return baseService.client.get(`${baseUrl}/${quotationId}`)
      .then(response => response.data);
  },
  
  /**
   * Download a file from the server
   * @param {string} fileId - The file ID to download
   * @param {string} originalFileName - The original file name to use for download
   * @returns {Promise} - Response with file data
   */
  downloadFile(fileId, originalFileName) {
    if (!fileId) {
      console.error('downloadFile: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    return baseService.client({
      url: `${baseUrl}/download/${fileId}`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'application/octet-stream'
      }
    }).then(response => {
      // Try to get filename from content-disposition header
      let filename = originalFileName;
      const contentDisposition = response.headers['content-disposition'];
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '') || filename;
        }
      }
      
      // Ensure we have a filename
      if (!filename) {
        console.warn('No filename found in headers, using original filename');
      }
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || originalFileName || 'download');
      
      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        filename: filename
      };
    });
  },
  
  /**
   * Delete a file from the server
   * @param {string} fileId - The file ID to delete
   * @returns {Promise} - Response with deletion result
   */
  deleteFile(fileId) {
    if (!fileId) {
      console.error('deleteFile: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    return baseService.client.delete(`${baseUrl}/${fileId}`)
      .then(response => response.data);
  },
  
  /**
   * Get file content as a blob for preview
   * @param {string} fileId - The file ID to retrieve
   * @returns {Promise} - Promise that resolves with the file blob
   */
  getFileBlob(fileId) {
    if (!fileId) {
      console.error('getFileBlob: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    return baseService.client({
      url: `${baseUrl}/download/${fileId}`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'application/octet-stream'
      }
    }).then(response => {
      // Get content type from headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create blob with the correct content type
      const blob = new Blob([response.data], { type: contentType });
      
      return {
        blob: blob,
        contentType: contentType,
        success: true
      };
    });
  },
  
  /**
   * Convert a file to base64 encoding
   * @param {File} file - The file object to convert
   * @returns {Promise} - Promise that resolves with the base64 content
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        // Get the base64 string (remove the data:mime/type;base64, prefix)
        const base64Content = reader.result.toString().split(',')[1];
        resolve(base64Content);
      };
      
      reader.onerror = error => {
        reject(error);
      };
      
      reader.readAsDataURL(file);
    });
  },
  
  /**
   * Prepare file upload DTOs from File objects
   * @param {Array} files - Array of File objects from input
   * @param {string} quotationId - The quotation ID or any row ID
   * @param {string} tableName - The table name to associate the files with
   * @returns {Promise} - Promise that resolves with array of DTOs ready for upload
   */
  prepareFileUploadDtos(files, quotationId, tableName = "Quotations") {
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    }
    
    const promises = Array.from(files).map(async file => {
      const base64Content = await this.fileToBase64(file);
      
      return {
        base64Content: base64Content,
        name: file.name,
        fileSize: file.size,
        fileType: file.type,
        rowID: quotationId,
        tableName: tableName,
        isStoredOnHardDisk: true
      };
    });
    
    return Promise.all(promises);
  },
  
  /**
   * Upload files using base64 encoding with JSON payload
   * @param {string} rowId - The row ID (e.g., tenantId, quotationId) to associate files with
   * @param {Array} files - Array of file objects with base64 content
   * @param {string} tableName - The table name to associate the files with (e.g., "UserProfiles", "Quotations")
   * @returns {Promise} - Response with uploaded file details
   */
  uploadFilesJsonAsync(rowId, files, tableName = "UserProfiles") {
    if (!rowId) {
      console.error('uploadFilesJsonAsync: rowId is required');
      return Promise.reject(new Error('Row ID is required'));
    }
    
    if (!files || !Array.isArray(files) || files.length === 0) {
      console.error('uploadFilesJsonAsync: files array is required');
      return Promise.reject(new Error('Files are required'));
    }
    
    // Ensure each file has the correct structure including tableName
    const filesWithTableName = files.map(file => ({
      ...file,
      rowID: rowId,
      tableName: tableName
    }));
    
    return baseService.client.post(`${baseUrl}/upload-json/${rowId}`, filesWithTableName)
      .then(response => response.data);
  },
  
  /**
   * Get a blob URL for previewing a file
   * @param {string} fileId - The file ID to preview
   * @param {string} fileName - The file name (for error reporting)
   * @returns {Promise} - Promise that resolves with the blob URL for the file
   */
  getFilePreviewUrl(fileId, fileName) {
    if (!fileId) {
      console.error('getFilePreviewUrl: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    
    return baseService.client({
      url: `${baseUrl}/download/${fileId}`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'application/octet-stream'
      }
    }).then(response => {
      // Create blob URL for preview
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/pdf'
      });
      
      const blobUrl = URL.createObjectURL(blob);
      
      return {
        blobUrl,
        blob,
        success: true,
        fileName: fileName
      };
    }).catch(error => {
      console.error('Error in getFilePreviewUrl:', error);
      throw error;
    });
  },

  /**
   * Get profile picture metadata
   * @returns {Promise} - Response with profile picture metadata
   */
  getProfilePictureMetadata() {
    return baseService.client.get(`${baseUrl}/profile-picture`)
      .then(response => response.data);
  },

  /**
   * Get profile picture as a blob with URL for displaying it
   * @returns {Promise} - Response with profile picture blob and URL
   */
  getProfilePicture() {
    return baseService.client({
      url: `${baseUrl}/profile-picture/download`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'image/*'
      }
    }).then(response => {
      // Create blob URL for preview
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'image/jpeg'
      });
      
      const blobUrl = URL.createObjectURL(blob);
      
      return {
        blobUrl,
        blob,
        success: true
      };
    }).catch(error => {
      console.error('Error getting profile picture:', error);
      // Return default values for when there's no profile picture yet
      return {
        blobUrl: null,
        blob: null,
        success: false
      };
    });
  },

  /**
   * Upload a profile picture
   * @param {File} file - The image file to upload
   * @returns {Promise} - Response with upload result
   */
  uploadProfilePicture(file) {
    if (!file) {
      console.error('uploadProfilePicture: file is required');
      return Promise.reject(new Error('File is required'));
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    return baseService.client({
      method: 'post',
      url: `${baseUrl}/profile-picture/upload`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(response => response.data);
  },

  /**
   * Convert Excel file to PDF for preview
   * @param {string} fileId - The file ID to convert
   * @returns {Promise} - Promise that resolves with the PDF blob
   */
  convertExcelToPdf(fileId) {
    if (!fileId) {
      console.error('convertExcelToPdf: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    return baseService.client({
      url: `${baseUrl}/convert-to-pdf/${fileId}`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    }).then(response => {
      // Create blob with PDF content type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      return {
        blob: blob,
        contentType: 'application/pdf',
        success: true
      };
    });
  },

  /**
   * Convert Word file to PDF for preview
   * @param {string} fileId - The file ID to convert
   * @returns {Promise} - Promise that resolves with the PDF blob
   */
  convertWordToPdf(fileId) {
    if (!fileId) {
      console.error('convertWordToPdf: fileId is required');
      return Promise.reject(new Error('File ID is required'));
    }
    
    return baseService.client({
      url: `${baseUrl}/convert-word-to-pdf/${fileId}`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    }).then(response => {
      // Create blob with PDF content type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      return {
        blob: blob,
        contentType: 'application/pdf',
        success: true
      };
    });
  }
};

export default fileManagementService; 